import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, Handshake, Award, Users, Target } from 'lucide-react';

const Partnerships = () => {
  const partnerLogos = [
    { name: 'QuickBooks', description: 'Authorized Implementation Partner' },
    { name: 'Zoho', description: 'Certified Partner & Consultant' },
    { name: 'Odoo', description: 'Official Implementation Partner' },
    { name: 'Microsoft Dynamics', description: 'Certified Solutions Partner' },
    { name: 'Tally', description: 'Authorized Reseller & Partner' }
  ];

  const benefits = [
    {
      icon: Award,
      title: 'Certified Expertise',
      description: 'Our team holds official certifications from all our partner platforms'
    },
    {
      icon: Users,
      title: 'Direct Support',
      description: 'Access to direct partner support channels for faster issue resolution'
    },
    {
      icon: Target,
      title: 'Best Practices',
      description: 'Implementation following official guidelines and industry best practices'
    },
    {
      icon: Handshake,
      title: 'Exclusive Benefits',
      description: 'Competitive pricing and exclusive offers through our partnerships'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Partnerships</h1>
            <p className="text-xl text-blue-100">
              Trusted by leading technology providers worldwide
            </p>
          </div>
        </div>
      </section>

      {/* Partners Grid Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technology Partners</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We are proud to be official partners and certified consultants for leading ERP and automation platforms
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {partnerLogos.map((partner, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-gradient-to-br from-white to-blue-50">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <Award className="h-10 w-10 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{partner.name}</h3>
                  <p className="text-gray-600 text-sm">{partner.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Partnership Benefits</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              What our partnerships mean for your business
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all text-center bg-white">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-4 mx-auto">
                      <IconComponent className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Partner With Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">Why Businesses Choose Our Partnerships</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Official Credentials</h3>
                  <p className="text-gray-600">
                    Our official partner status ensures you get authentic, fully-supported implementations 
                    with access to the latest features and updates.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Vendor Support</h3>
                  <p className="text-gray-600">
                    Direct line to vendor support teams means faster issue resolution and access to 
                    advanced technical resources when needed.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Continuous Training</h3>
                  <p className="text-gray-600">
                    Our team receives ongoing training and updates directly from partners, ensuring 
                    we're always up-to-date with the latest capabilities.
                  </p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Better Pricing</h3>
                  <p className="text-gray-600">
                    Our partnerships allow us to offer competitive pricing and exclusive deals that 
                    you won't find elsewhere.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Distribution Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Technology Distribution</h2>
            <p className="text-xl text-gray-600 mb-8">
              As authorized resellers, we provide comprehensive licensing, implementation, and support 
              services for all our partner platforms. We also distribute pre-built, industry-ready 
              solutions from trusted global technology developers.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-6 bg-white rounded-xl shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Software Licensing</h3>
                <p className="text-gray-600 text-sm">Official licenses with full support</p>
              </div>
              <div className="p-6 bg-white rounded-xl shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Implementation</h3>
                <p className="text-gray-600 text-sm">Expert setup and configuration</p>
              </div>
              <div className="p-6 bg-white rounded-xl shadow-lg">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Ongoing Support</h3>
                <p className="text-gray-600 text-sm">Continuous assistance and updates</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-blue-100">
            Leverage our partnerships to get the best technology solutions for your business
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6">
              Contact Us Today <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Partnerships;